import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Fingerprint, Target, Rocket, Briefcase, GraduationCap, DollarSign, Users, ClipboardList,
  ArrowRight, Play, Award, Backpack, Shield, Footprints, HeartHandshake, Layers,
  Sparkles, Building2, Lock, Compass,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   The Living FuturePath, CSS/SVG, every word selectable and editable.
   Illustrative of what the platform generates. Deliberately no user counts,
   no testimonials and no company statistics: nothing here claims traction.
   ═════════════════════════════════════════════════════════════════════════ */

const MODULES = [
  { key: 'identity', label: 'IDENTITY', icon: Fingerprint, hue: '#34d399',
    lines: ['Builder • Strategist', 'Creator • Leader'], pos: 'md:col-start-1 md:row-start-1' },
  { key: 'skills', label: 'SKILLS', icon: Target, hue: '#fbbf24',
    lines: ['The few skills between you', 'and your next level'], pos: 'md:col-start-1 md:row-start-2' },
  { key: 'network', label: 'NETWORK', icon: Users, hue: '#22d3ee',
    lines: ['Mentor opportunities', 'identified for you'], pos: 'md:col-start-1 md:row-start-3' },
  { key: 'future', label: 'FUTURE OF WORK', icon: Rocket, hue: '#a78bfa',
    lines: ['Emerging opportunities', 'detected in your field'], pos: 'md:col-start-1 md:row-start-4' },
  { key: 'career', label: 'CAREER PATHS', icon: Briefcase, hue: '#38bdf8',
    lines: ['AI Product Strategy', 'Strong FuturePath fit'], pos: 'md:col-start-3 md:row-start-1' },
  { key: 'education', label: 'EDUCATION', icon: GraduationCap, hue: '#c084fc',
    lines: ['College • Certification', 'Apprenticeship • Bootcamp'], pos: 'md:col-start-3 md:row-start-2' },
  { key: 'financial', label: 'FINANCIAL FUTURE', icon: DollarSign, hue: '#2dd4bf',
    lines: ['Projected lifetime', 'financial trajectory'], pos: 'md:col-start-3 md:row-start-3' },
  { key: 'action', label: 'ACTION PLAN', icon: ClipboardList, hue: '#f59e0b',
    lines: ['Your next best move', 'is ready'], pos: 'md:col-start-3 md:row-start-4' },
];

/* Bright, forward-walking figures on the glowing path, one per kind of journey. */
const FIGURES = {
  student: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/bf7b3608a4fa2c13da9e2d392053eaaa9d6573b3236e0cad9e3b76993c614343.jpeg',
  college: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/4b2b1a3914f8d8a0853a813812c62863dc6234a3f6370e2a348312619f2c03d5.jpeg',
  professional: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/c5b60d6541412fd946ea8564de364213941d6f1f245b9edbf27872300df7737c.jpeg',
  athletic: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/2a133234653090bf0d9c1d5e3d0e8942b02c8107f82cdb76eabc297cf0aac67a.jpeg',
  military: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/21fcbac2c2a023e544a9263f6291a52c277f23171ebed7b4243df157f8d98960.jpeg',
  athlete: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/fbf944d07f6f1cf24e14dfd051f19e232482fe2f53d530bab727a1aba1dc32ee.jpeg',
  second_chance: 'https://static.prod-images.emergentagent.com/jobs/36a5d7cd-35fc-4e22-bd01-2472beb25c58/images/9fc8fa60125439dcf0cebb3328c1d4f0cf1ed14e428f107a9b6b102281c847d5.jpeg',
};

const JOURNEYS = [
  { key: 'high_school', label: 'High School\nStudent', icon: GraduationCap, hue: '#c084fc', figure: 'student',
    steps: ['Discover Me', 'College', 'Career', 'Skills', 'Funding', 'Mentors'] },
  { key: 'college', label: 'College\nStudent', icon: Backpack, hue: '#38bdf8', figure: 'college',
    steps: ['Major', 'Skills', 'Internships', 'Career', 'Network', 'Financial Future'] },
  { key: 'professional', label: 'Career\nProfessional', icon: Briefcase, hue: '#fbbf24', figure: 'professional',
    steps: ['Current Skills', 'Future of Work', 'Career Options', 'Skill Gaps', 'Network', 'Action Plan'] },
  { key: 'military', label: 'Military\nTransition', icon: Shield, hue: '#34d399', figure: 'military',
    steps: ['Military Experience', 'Transferable Skills', 'Civilian Careers', 'Skill Gaps', 'Certifications', 'Employers'] },
  { key: 'athlete', label: 'Athlete', icon: Footprints, hue: '#22d3ee', figure: 'athlete',
    steps: ['Athletic Identity', 'Transferable Skills', 'Education', 'Career', 'Personal Brand', 'Network'] },
  { key: 'second_chance', label: 'Second\nChance', icon: HeartHandshake, hue: '#fb7185', figure: 'second_chance',
    steps: ['Current Position', 'Skills', 'Opportunity', 'Training', 'Support Network', 'Career Path'] },
];

const CAPABILITIES = [
  { icon: Layers, title: 'EVERY LIFE TRANSITION', line: "One intelligence platform across life's pivotal moments", hue: '#38bdf8' },
  { icon: Compass, title: '360° PATH INTELLIGENCE', line: 'Identity • Skills • Education • Career • Financial Future', hue: '#a78bfa' },
  { icon: Sparkles, title: 'AI-POWERED', line: 'Personalized pathways that evolve with you', hue: '#22d3ee' },
  { icon: Building2, title: 'BUILT FOR PEOPLE + ORGANIZATIONS', line: 'Individuals • Families • Schools • Workforce Programs', hue: '#34d399' },
  { icon: Lock, title: 'YOUR DATA. YOUR FUTURE.', line: 'Privacy-centered by design', hue: '#fbbf24' },
];

const STEPS = [
  { n: 1, title: 'Who am I?', line: 'Identity, values, strengths and how you work best.', hue: '#22d3ee' },
  { n: 2, title: 'The life I want', line: 'The life you are aiming at, and the income it takes.', hue: '#38bdf8' },
  { n: 3, title: "What's possible?", line: 'Careers, college, trades, military, entrepreneurship.', hue: '#60a5fa' },
  { n: 4, title: "What's changing?", line: 'How AI and the market are reshaping your field.', hue: '#818cf8' },
  { n: 5, title: "What's my best path?", line: 'Options compared on fit, opportunity, ROI and lifestyle.', hue: '#a78bfa' },
  { n: 6, title: 'Who pays for it?', line: 'Grants, scholarships, benefits and what you can afford.', hue: '#c084fc' },
  { n: 7, title: 'How do I launch?', line: 'Readiness, proof of skill, mentors and employers.', hue: '#f0abfc' },
  { n: 8, title: 'What do I do next?', line: 'A dated plan that rebuilds itself when life changes.', hue: '#fbbf24' },
];

/** Motes of light drifting through the visualization. */
const Motes = ({ count = 18 }) => (
  <div aria-hidden className="pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-[2rem]">
    {Array.from({ length: count }).map((_, i) => {
      const size = 1.5 + ((i * 7) % 4);
      const hue = ['#67e8f9', '#a78bfa', '#38bdf8', '#c4b5fd'][i % 4];
      return (
        <span key={i} className="fp-mote"
          style={{
            left: `${(i * 37) % 96}%`,
            top: `${20 + ((i * 53) % 75)}%`,
            width: `${size}px`, height: `${size}px`,
            background: hue,
            boxShadow: `0 0 ${size * 4}px ${hue}`,
            '--dx': `${((i % 5) - 2) * 14}px`,
            '--dy': `${-60 - ((i * 11) % 70)}px`,
            '--dur': `${9 + ((i * 3) % 9)}s`,
            animationDelay: `${(i * 0.7) % 9}s`,
          }} />
      );
    })}
  </div>
);

const ModuleCard = ({ m, delay, active, reduced }) => (
  <motion.div
    initial={reduced ? false : { opacity: 0, y: 14, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.55, ease: 'easeOut' }}
    whileHover={reduced ? {} : { y: -4 }}
    className={`group relative rounded-xl border backdrop-blur-md px-3.5 py-3 ${m.pos} self-center`}
    style={{
      borderColor: active ? `${m.hue}99` : `${m.hue}3d`,
      background: `linear-gradient(150deg, ${m.hue}1a, rgba(8,15,30,0.72))`,
      boxShadow: active ? `0 0 34px -12px ${m.hue}aa` : `0 0 22px -16px ${m.hue}88`,
    }}
    data-testid={`hero-module-${m.key}`}>
    <div className="flex items-center gap-2">
      <m.icon className="w-4 h-4 shrink-0" style={{ color: m.hue }} />
      <p className="text-[11px] font-bold tracking-[0.12em]" style={{ color: m.hue }}>{m.label}</p>
    </div>
    {m.lines.map((l) => (
      <p key={l} className="text-[11.5px] leading-snug text-slate-300/90 mt-0.5">{l}</p>
    ))}
  </motion.div>
);

const LivingFuturePath = ({ journey, reduced }) => (
  <div className="relative" data-testid="living-futurepath">
    {/* horizon */}
    <div aria-hidden className="fp-frame-glow absolute -inset-6 z-0 overflow-hidden rounded-[2rem] bg-[#040a18]/60">
      <div className="absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-500/25 blur-[90px]" />
      <div className="absolute left-1/2 bottom-0 h-40 w-[130%] -translate-x-1/2 bg-gradient-to-t from-cyan-500/20 to-transparent blur-2xl" />
      <AnimatePresence mode="sync">
        <motion.img key={journey.figure} src={FIGURES[journey.figure]} alt=""
          initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.0 }}
          animate={reduced ? { opacity: 0.95 } : { opacity: 0.95, scale: [1.0, 1.16] }}
          exit={{ opacity: 0, transition: { duration: 0.7 } }}
          transition={reduced ? { duration: 0.9 }
            : { opacity: { duration: 1.1 },
                scale: { duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'linear' } }}
          className="absolute inset-0 h-full w-full select-none object-cover"
          data-testid="hero-journey-figure"
          style={{ maskImage: 'radial-gradient(125% 118% at 50% 55%, #000 62%, transparent 100%)',
                   WebkitMaskImage: 'radial-gradient(125% 118% at 50% 55%, #000 62%, transparent 100%)' }} />
      </AnimatePresence>
      <div className="absolute inset-x-0 bottom-0 h-1/2 opacity-[0.05]"
        style={{ backgroundImage: 'linear-gradient(rgba(120,200,255,0.5) 1px, transparent 1px)', backgroundSize: '100% 26px' }} />
    </div>

    <Motes />

    {/* Pathways from YOU to every intelligence module, the connective tissue. */}
    <svg aria-hidden viewBox="0 0 100 100" preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 z-[1] hidden h-full w-full md:block">
      <defs>
        <linearGradient id="fpPath" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#67e8f9" stopOpacity="1" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      {[[18, 18], [50, 12], [82, 18], [18, 50], [82, 50], [18, 82], [50, 88], [82, 82]].map(([x, y], i) => (
        <path key={i} d={`M 50 50 Q ${(50 + x) / 2} ${y} ${x} ${y}`}
          fill="none" stroke="url(#fpPath)" strokeWidth="0.55"
          strokeDasharray="2 3" className={reduced ? '' : 'fp-flow'}
          style={{ animationDelay: `${i * 0.35}s` }} />
      ))}
    </svg>

    <div className="relative z-[2] grid gap-3 md:min-h-[440px] md:grid-cols-[minmax(0,190px)_minmax(0,1fr)_minmax(0,190px)] md:grid-rows-4 md:gap-x-6 md:gap-y-3 place-items-stretch">
      {MODULES.slice(0, 3).map((m, i) => (
        <ModuleCard key={m.key} m={m} delay={0.25 + i * 0.09} reduced={reduced} />
      ))}

      {/* ── The centre: the figure shows through; YOU sits low, clear of the face ── */}
      <div className="md:col-start-2 md:row-start-1 md:row-span-4 relative flex items-end justify-center py-2">
        <motion.div
          initial={reduced ? false : { opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.7, ease: 'easeOut' }}
          className="relative z-[3] flex h-[112px] w-[112px] items-center justify-center rounded-full text-center backdrop-blur-[2px]"
          style={{
            background: 'radial-gradient(circle at 50% 45%, rgba(167,139,250,0.30), rgba(8,15,35,0.72) 62%, rgba(2,8,23,0.86) 80%)',
            border: '1px solid rgba(167,139,250,0.55)',
            boxShadow: '0 0 70px -12px rgba(139,92,246,0.75), inset 0 0 42px -14px rgba(34,211,238,0.7)',
          }}
          data-testid="hero-you-node">
          {!reduced && (
            <span aria-hidden className="absolute inset-0 rounded-full border border-cyan-300/40 fp-pulse-ring" />
          )}
          <div className="px-4">
            <p className="font-outfit text-2xl font-black tracking-tight text-white">YOU</p>
            <p className="text-[11px] leading-relaxed text-cyan-100/85 mt-1">
              Your Future.<br />Your Path.<br />Your Choice.
            </p>
          </div>
        </motion.div>
      </div>

      {MODULES.slice(3).map((m, i) => (
        <ModuleCard key={m.key} m={m} delay={0.52 + i * 0.09} reduced={reduced} />
      ))}
    </div>

    <p className="relative z-[2] mx-auto mt-10 w-fit rounded-full bg-[#040a18]/80 px-3 py-1 text-center text-[10.5px] text-slate-300">
      Illustrative of the intelligence FuturePath generates. Not a real person's results.
    </p>
  </div>
);

export const HeroFuturePath = () => {
  const reduced = useReducedMotion();
  const [journeyKey, setJourneyKey] = useState('high_school');
  const [autoplay, setAutoplay] = useState(true);
  const [liveStep, setLiveStep] = useState(0);
  const [liveCap, setLiveCap] = useState(0);
  const journey = useMemo(() => JOURNEYS.find((j) => j.key === journeyKey), [journeyKey]);

  useEffect(() => {
    const id = setInterval(() => setLiveCap((c) => (c + 1) % CAPABILITIES.length), 1600);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setLiveStep((s) => (s + 1) % (STEPS.length + 1)), 900);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setInterval(() => {
      setJourneyKey((k) => {
        const i = JOURNEYS.findIndex((j) => j.key === k);
        return JOURNEYS[(i + 1) % JOURNEYS.length].key;
      });
    }, 5000);
    return () => clearInterval(id);
  }, [autoplay]);

  return (
    <section id="hero" className="relative overflow-hidden pt-24 pb-14 px-5 md:px-10">
      <div aria-hidden className="absolute inset-0 z-0 bg-[#03081a]">
        <div className="absolute -top-48 left-1/4 h-[760px] w-[760px] rounded-full bg-gradient-to-br from-cyan-400/20 via-blue-600/12 to-transparent blur-3xl" />
        <div className="absolute -bottom-56 right-1/5 h-[720px] w-[720px] rounded-full bg-gradient-to-tl from-violet-600/22 via-indigo-600/10 to-transparent blur-3xl" />
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.14) 1px, transparent 1px)', backgroundSize: '58px 58px' }} />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)]">
          {/* ── Left ── */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="fp-badge-glow inline-flex w-full max-w-md items-center justify-center gap-2 rounded-full border border-cyan-300/45 bg-cyan-400/[0.09] px-4 py-2"
                data-testid="award-badge">
                <Award className="w-4 h-4 text-cyan-300 shrink-0" />
                <span className="text-[12.5px] font-semibold tracking-wide text-cyan-100">AI NextGen Futurist Awards</span>
                <span className="h-3 w-px bg-cyan-300/40" />
                <span className="text-[12.5px] text-slate-200/90">2026 Nominee</span>
              </div>
            </div>

            <h1 className="font-outfit mt-6 text-4xl font-black leading-[1.03] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Navigate Your<br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-violet-400 bg-clip-text text-transparent">
                Infinite Potential.
              </span>
            </h1>

            <p className="mt-5 text-base font-semibold tracking-[0.06em] text-slate-300 md:text-lg">
              AI-POWERED{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-violet-400 bg-clip-text text-transparent">
                DECISION INTELLIGENCE
              </span>{' '}
              FOR LIFE.
            </p>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300/90">
              Discover who you are. See where the world is going. Compare the paths available to you.
              Then build the skills, education, network and financial plan to get there.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-primary"
                className="fp-cta-glow group relative inline-flex items-center gap-2 overflow-hidden rounded-xl px-8 py-4 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-500" />
                <span aria-hidden className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 bg-gradient-to-r from-cyan-500 via-blue-600 to-violet-600" />
                <span className="relative">Build My Future</span>
                <ArrowRight className="relative w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>

              <Link to="/how-it-works" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-7 py-4 text-base font-semibold text-white transition-colors duration-200 hover:border-cyan-300/60 hover:bg-white/[0.06]">
                <Play className="w-4 h-4" /> See How It Works
              </Link>
            </div>
          </motion.div>

          {/* ── Right: the living visualization ── */}
          <LivingFuturePath journey={journey} reduced={reduced} />
        </div>

        {/* ── Who are you becoming? ── */}
        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm"
          data-testid="journey-selector">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="lg:w-56 shrink-0">
              <h2 className="font-outfit text-lg font-semibold text-white md:text-lg">Who are you becoming?</h2>
              <p className="mt-1 text-sm text-slate-300">Select your journey to see your path.</p>
            </div>
            <div className="grid flex-1 grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
              {JOURNEYS.map((j) => {
                const on = j.key === journeyKey;
                return (
                  <button key={j.key} onClick={() => { setAutoplay(false); setJourneyKey(j.key); }}
                    aria-pressed={on} data-testid={`journey-${j.key}`}
                    className="group rounded-xl border px-3 py-3.5 text-center transition-all duration-200 hover:-translate-y-0.5"
                    style={{
                      borderColor: on ? j.hue : 'rgba(148,163,184,0.22)',
                      background: on ? `${j.hue}18` : 'rgba(255,255,255,0.02)',
                      boxShadow: on ? `0 0 26px -10px ${j.hue}` : 'none',
                    }}>
                    <j.icon className="mx-auto h-5 w-5" style={{ color: on ? j.hue : '#94a3b8' }} />
                    <span className="mt-2 block whitespace-pre-line text-[12px] font-semibold leading-tight"
                      style={{ color: on ? '#fff' : '#cbd5e1' }}>{j.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── One platform. Every pivotal transition. ── */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm"
          data-testid="capability-bar">
          <div className="grid gap-4 lg:grid-cols-6 lg:divide-x lg:divide-white/10">
            <div className="lg:pr-4">
              <p className="font-outfit text-sm font-bold leading-snug text-white">
                ONE PLATFORM.<br />EVERY PIVOTAL TRANSITION.
              </p>
            </div>
            {CAPABILITIES.map((c, i) => {
              const now = i === liveCap;
              return (
                <motion.div key={c.title} className="rounded-lg lg:px-4"
                  animate={{
                    backgroundColor: now ? `${c.hue}12` : 'rgba(0,0,0,0)',
                    boxShadow: now ? `0 0 26px -14px ${c.hue}` : '0 0 0 rgba(0,0,0,0)',
                  }}
                  transition={{ duration: 0.55, ease: 'easeOut' }}
                  data-testid={`capability-${i}`}>
                  <motion.div animate={{ scale: now ? 1.15 : 1 }} transition={{ duration: 0.5 }}
                    style={{ originX: 0, originY: 0.5 }}>
                    <c.icon className="h-4 w-4" style={{ color: c.hue }} />
                  </motion.div>
                  <motion.p className="mt-1.5 text-[11.5px] font-bold tracking-[0.08em]"
                    animate={{ color: c.hue, textShadow: now ? `0 0 14px ${c.hue}` : `0 0 0px ${c.hue}00` }}
                    transition={{ duration: 0.55 }}>{c.title}</motion.p>
                  <motion.p className="mt-1 text-[11.5px] leading-snug"
                    animate={{ color: now ? 'rgba(226,232,240,0.95)' : 'rgba(148,163,184,0.85)' }}
                    transition={{ duration: 0.55 }}>{c.line}</motion.p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ── The system ── */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-gradient-to-br from-blue-950/40 via-slate-900/30 to-violet-950/30 p-6 backdrop-blur-sm"
          data-testid="five-step-system">
          <p className="text-center text-[11px] font-bold tracking-[0.18em] text-violet-300">
            THE AGENTIC FUTUREPATH AI SYSTEM
          </p>
          <h2 className="font-outfit mt-1 text-center text-xl font-semibold text-white md:text-2xl">
            From Insight to Impact in{' '}
            <span className="bg-gradient-to-r from-cyan-300 to-violet-400 bg-clip-text text-transparent">8 Steps</span>
          </h2>

          <div className="relative mt-6">
            <span aria-hidden className="absolute left-4 right-4 top-4 hidden h-px bg-gradient-to-r from-cyan-400/50 via-violet-400/50 to-amber-300/40 lg:block" />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => {
                const lit = i <= liveStep;
                const now = i === liveStep;
                return (
                  <div key={s.n} className="relative" data-testid={`hero-step-${s.n}`}>
                    <motion.span
                      className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
                      animate={{
                        background: lit ? s.hue : 'rgba(148,163,184,0.18)',
                        color: lit ? '#020617' : '#94a3b8',
                        boxShadow: now ? `0 0 30px -2px ${s.hue}, 0 0 0 6px ${s.hue}22`
                          : lit ? `0 0 18px -6px ${s.hue}` : 'none',
                        scale: now ? 1.16 : 1,
                      }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}>{s.n}</motion.span>
                    <motion.p className="mt-2.5 text-sm font-semibold"
                      animate={{ color: lit ? s.hue : '#94a3b8' }} transition={{ duration: 0.45 }}>
                      {s.title}
                    </motion.p>
                    <motion.p className="mt-1 text-xs leading-relaxed"
                      animate={{ color: lit ? 'rgba(203,213,225,0.95)' : 'rgba(148,163,184,0.65)' }}
                      transition={{ duration: 0.45 }}>{s.line}</motion.p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="mx-auto mt-6 max-w-3xl text-center text-sm text-slate-300/85">
            Most platforms help people choose a career.{' '}
            <span className="text-white font-medium">Agentic FuturePath AI helps people navigate the
            decisions that shape their future</span>, one intelligence layer connecting who you are,
            where the world is going, and what you do next.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroFuturePath;
